import { useEffect, useState } from "react";
import api from "../lib/api";
import { Loading, Avatar } from "../components/common";
import { gameLabel } from "./Dashboard";
import { Swords } from "lucide-react";

export default function HeadToHead() {
  const [players, setPlayers] = useState([]);
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const p = (await api.get("/players")).data;
      setPlayers(p);
      if (p.length >= 2) {
        setP1(p[0].id);
        setP2(p[1].id);
      }
    })();
  }, []);

  useEffect(() => {
    if (p1 && p2 && p1 !== p2) {
      setLoading(true);
      api.get("/head-to-head", { params: { p1, p2 } }).then((r) => {
        setData(r.data);
        setLoading(false);
      });
    }
  }, [p1, p2]);

  const Select = ({ value, onChange, exclude, testid }) => (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white font-bold focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
    >
      {players.map((p) => (
        <option key={p.id} value={p.id} disabled={p.id === exclude}>
          {p.name}
        </option>
      ))}
    </select>
  );

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-2">Rivalry</div>
        <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-none">
          Head to Head
        </h1>
      </div>

      {players.length < 2 ? (
        <p className="text-slate-500">Add at least 2 players to compare rivalries.</p>
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={p1} onChange={setP1} exclude={p2} testid="h2h-p1-select" />
            <Swords className="w-6 h-6 text-[#FF3B30]" />
            <Select value={p2} onChange={setP2} exclude={p1} testid="h2h-p2-select" />
          </div>

          {loading || !data ? (
            <Loading label="Loading rivalry" />
          ) : (
            <>
              <div className="panel rounded-sm p-8">
                <div className="grid grid-cols-3 items-center">
                  <PlayerSide name={data.p1.name} wins={data.p1_wins} accent="#007AFF" />
                  <div className="text-center">
                    <div className="text-xs font-bold uppercase text-slate-500 mb-1">
                      {data.matches_played} Matches
                    </div>
                    <div className="font-display text-2xl font-black text-slate-600">VS</div>
                  </div>
                  <PlayerSide name={data.p2.name} wins={data.p2_wins} accent="#FF3B30" right />
                </div>
              </div>

              <div className="grid gap-1 md:grid-cols-3">
                <MiniStat label={`${data.p1.name} Win %`} value={`${data.p1_win_pct}%`} />
                <MiniStat label="Legs Won" value={`${data.p1_legs} - ${data.p2_legs}`} />
                <MiniStat label={`${data.p1.name} 501 Avg vs`} value={data.p1_501_avg_vs} />
                <MiniStat label={`${data.p1.name} 180s vs`} value={data.p1_180s_vs} />
                <MiniStat label={`${data.p1.name} High CO vs`} value={data.p1_highest_checkout_vs} />
                <MiniStat
                  label="By Game"
                  value={
                    Object.keys(data.by_game).length
                      ? Object.entries(data.by_game)
                          .map(([g, v]) => `${g === "501" ? "501" : "ATW"} ${v.p1}-${v.p2}`)
                          .join("  ")
                      : "—"
                  }
                  small
                />
              </div>

              {data.recent.length > 0 && (
                <div>
                  <h2 className="font-display text-2xl font-bold uppercase text-white mb-3">
                    Recent Meetings
                  </h2>
                  <div className="flex flex-wrap gap-1">
                    {data.recent.map((m, i) => {
                      const winnerName =
                        m.winner_id === data.p1.id ? data.p1.name : data.p2.name;
                      const isP1 = m.winner_id === data.p1.id;
                      return (
                        <div
                          key={i}
                          className="panel rounded-sm px-3 py-2 flex items-center gap-2"
                          data-testid={`meeting-${i}`}
                        >
                          <span
                            className="font-display text-lg font-black"
                            style={{ color: isP1 ? "#007AFF" : "#FF3B30" }}
                          >
                            {winnerName}
                          </span>
                          <span className="text-xs font-bold uppercase text-slate-500">
                            W · {gameLabel(m.game_type)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function PlayerSide({ name, wins, accent, right }) {
  return (
    <div className={`flex flex-col items-center ${right ? "md:items-end" : "md:items-start"}`}>
      <Avatar name={name} size={56} />
      <div className="font-display text-2xl font-bold text-white mt-2 text-center">{name}</div>
      <div className="font-display text-6xl font-black leading-none" style={{ color: accent }}>
        {wins}
      </div>
    </div>
  );
}

function MiniStat({ label, value, small }) {
  return (
    <div className="panel rounded-sm p-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className={`font-display font-extrabold text-white mt-1 ${small ? "text-lg" : "text-3xl"}`}>
        {value}
      </div>
    </div>
  );
}
